/**
 * This service handles enrollment data consistency across the application
 * ensuring the same enrollment numbers are shown for the same playbook
 * throughout a user session.
 */

const SESSION_VARIANCE_KEY = 'playbook_enrollment_variance';
const SESSION_AVATARS_KEY = 'playbook_enrollment_avatars';

// List of names for generating initials and random avatars
const names = [
  'John Smith', 'Anna Lee', 'Michael Chen', 'Sarah Johnson', 'David Kim',
  'Maria Garcia', 'James Brown', 'Olivia Wilson', 'Robert Taylor', 'Emily Davis',
  'William Martin', 'Sofia Rodriguez', 'Thomas White', 'Ava Martinez', 'Joseph Anderson',
  'Emma Thompson', 'Daniel Moore', 'Mia Jackson', 'Alex Turner', 'Zoe Williams'
];

/**
 * Gets a consistent random variance for a playbook ID that persists
 * throughout the user's session
 */
export const getEnrollmentVariance = (playbookId: string): number => {
  try {
    // Try to get existing variances from localStorage
    const storedVariances = localStorage.getItem(SESSION_VARIANCE_KEY);
    const variances = storedVariances ? JSON.parse(storedVariances) : {};
    
    // If we already have a variance for this playbook, return it
    if (variances[playbookId] !== undefined) {
      return variances[playbookId];
    }
    
    // Generate a new random variance between -10 and +10
    const newVariance = Math.floor(Math.random() * 21) - 10;
    
    // Store it for future use
    variances[playbookId] = newVariance;
    localStorage.setItem(SESSION_VARIANCE_KEY, JSON.stringify(variances));
    
    return newVariance;
  } catch (e) {
    // If localStorage fails, just return a random number
    // This ensures the app still works even if localStorage is unavailable
    console.warn('Failed to access localStorage for enrollment variance:', e);
    return Math.floor(Math.random() * 21) - 10;
  }
};

/**
 * Gets the consistent enrolled users count for a playbook
 * This function uses the same logic in both list and detail views
 */
export const getEnrolledUsersCount = (
  playbookId: string, 
  displayOrder: number = 0,
  overrideCount?: number
): number => {
  // If an override count is provided, use it
  if (overrideCount !== undefined) {
    return overrideCount;
  }
  
  // Get the base count based on display order
  let baseCount = 0;
  if (displayOrder === 0) baseCount = 85; // First playbook: 80+
  else if (displayOrder === 1) baseCount = 54; // Second playbook: 50+
  else if (displayOrder === 2) baseCount = 41; // Building Momentum: 35-47
  else if (displayOrder === 3) baseCount = 23; // Written Content Cluster: 16-30
  else if (displayOrder === 4) baseCount = 38; // Lead Magnet & Email Engine: 30-46
  else if (displayOrder === 5) baseCount = 14; // Video Content Cluster: 8-21
  else if (displayOrder === 6) baseCount = 21; // Paid Acquisition & Partnership: 13-30
  else if (displayOrder === 7) baseCount = 77; // Retention & Expansion Engine: 65-89
  else if (displayOrder === 8) baseCount = 52; // Viral Amplification Engine: 40-65
  else baseCount = 15; // Default for any other playbooks
  
  // Apply consistent variance but ensure count doesn't go below 5
  const variance = getEnrollmentVariance(playbookId);
  return Math.max(5, baseCount + variance);
};

/**
 * Generate a seed number from a string (playbook ID)
 * This helps create consistent "random" selections for the same playbook
 */
const generateSeed = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Returns a consistent but different set of avatar URLs for each playbook
 * Mixes real avatar images with initial-based avatars
 */
export const getEnrolledUserAvatars = (playbookId: string): { url: string; type: 'image' | 'initials'; initials?: string }[] => {
  try {
    // Try to get existing avatar selections from localStorage
    const storedAvatars = localStorage.getItem(SESSION_AVATARS_KEY);
    const avatarsMap = storedAvatars ? JSON.parse(storedAvatars) : {};
    
    // If we already have avatars for this playbook, return them
    if (avatarsMap[playbookId]) {
      return avatarsMap[playbookId];
    }
    
    // Generate a seed from the playbook ID for consistent "randomness"
    const seed = generateSeed(playbookId);
    
    // Select 5 random but consistent user indexes based on the seed
    const selectedIndexes: number[] = [];
    let rng = seed;
    
    while (selectedIndexes.length < 5) {
      // Simple LCG-like random number generation for reproducibility
      rng = (rng * 1664525 + 1013904223) % 4294967296;
      const index = rng % names.length;
      if (!selectedIndexes.includes(index)) {
        selectedIndexes.push(index);
      }
    }
    
    // Create the avatars (mix of real images and initials)
    const avatars = selectedIndexes.map((index, position) => {
      const name = names[index];
      
      // Generate initials from the name
      const nameParts = name.split(' ');
      const initials = nameParts.map(part => part[0]).join('');
      
      // Vary between real avatars and initials (based on position + seed)
      const useInitials = ((seed + position) % 3 === 0); // Roughly 1/3 will be initials
      
      if (useInitials) {
        // Use initials with random background color
        const colorSeed = (seed + index) % 999; // Generate different colors
        return {
          url: `https://ui-avatars.com/api/?name=${name}&background=random&seed=${colorSeed}`,
          type: 'initials' as const,
          initials
        };
      } else {
        // Use a real avatar image with a fixed seed to keep it consistent
        // Use different image sets (img parameter) based on position for variety
        const imgSet = ((seed + index) % 70) + 1; // pravatar has images from 1-70
        return {
          url: `https://i.pravatar.cc/150?img=${imgSet}&u=${playbookId}-${index}`,
          type: 'image' as const
        };
      }
    });
    
    // Store the avatars for future use
    avatarsMap[playbookId] = avatars;
    localStorage.setItem(SESSION_AVATARS_KEY, JSON.stringify(avatarsMap));
    
    return avatars;
  } catch (e) {
    // Fallback if localStorage fails
    console.warn('Failed to access localStorage for enrollment avatars:', e);
    
    return [
      { url: 'https://i.pravatar.cc/150?img=1', type: 'image' },
      { url: 'https://i.pravatar.cc/150?img=2', type: 'image' },
      { url: 'https://ui-avatars.com/api/?name=JD&background=random', type: 'initials', initials: 'JD' },
      { url: 'https://i.pravatar.cc/150?img=4', type: 'image' },
      { url: 'https://ui-avatars.com/api/?name=MS&background=random', type: 'initials', initials: 'MS' }
    ];
  }
};

/**
 * Returns consistent demo enrollment data for a playbook
 */
export const getDemoEnrollmentData = (
  playbookId: string, 
  displayOrder: number = 0,
  overrideCount?: number
) => {
  return {
    avatars: getEnrolledUserAvatars(playbookId),
    count: getEnrolledUsersCount(playbookId, displayOrder, overrideCount)
  };
}; 